import { getEnumKeyByEnumValue } from 'global-services/OhsDataParse';
import { SysField } from 'injury-mgmt/models/OhsInjuryMgmtRecord';
import { ModuleType } from 'global-services/constants/OhsObject';

import { OhsCusval, OhsCusvalType } from '../cusvals/OhsCusvalModel';
import { OhsSysFieldDefinition, OhsSysFieldValue } from './OhsSysFieldModel';
import { formatCusvalValueToPayload } from '../cusvals/OhsCusvalServices';

// added to handle inconsistencies on BE side
// be side has different spelling for the same field
export const formatSysFieldToCusvalType = (type: OhsCusvalType | string): OhsCusvalType => {
  switch (type) {
    case 'multi_select':
      return OhsCusvalType.MutipleSelect;
    case 'single_select':
      return OhsCusvalType.Select;
    default:
      return type as OhsCusvalType;
  }
};

export const formatSysFieldDefToCusval = (sysFieldData: OhsSysFieldDefinition): OhsCusval => {
  const opts = sysFieldData?.options?.listItems || [];
  const formattedData: OhsCusval = {
    title: sysFieldData?.name,
    description: sysFieldData?.description,
    _id: sysFieldData?._id,
    type: formatSysFieldToCusvalType(sysFieldData.fieldType),
    typeDisplay: sysFieldData?.fieldType
      ? formatSysFieldToCusvalType(getEnumKeyByEnumValue(OhsCusvalType, sysFieldData?.fieldType))
      : undefined,
    options: opts.map((opt) => ({ name: opt.title })),
    required: false,
    requiredDisplay: 'No',
    moduleType: sysFieldData?.options?.moduleType,
  };
  return formattedData;
};

export const formatSysFieldToCusval = (sysFieldData: SysField): OhsCusval => {
  const formattedData: OhsCusval = {
    _id: sysFieldData?._id,
    type: formatSysFieldToCusvalType(sysFieldData.fieldType),
    title: sysFieldData?.name,
    required: sysFieldData.required,
  };
  return formattedData;
};

export const separateSysFieldAndCusvals = ({
  values,
  sysFieldConfig,
  cusvalConfig,
}: {
  values: any;
  sysFieldConfig: SysField[];
  cusvalConfig: OhsCusval[];
}) => {
  const sysFieldIds = sysFieldConfig?.map((item) => item?._id);

  const makeArray = (source: any, ids: string[]) => {
    return ids
      .map((id) => ({ _id: id, value: source[id] }))
      .filter((item) => item.value !== undefined);
  };

  const sysFields: OhsSysFieldValue[] = (makeArray(values, sysFieldIds) || [])
    .map((item) => {
      let itemValue = item?.value;
      const itemType = Array.isArray(item?.value) ? item.value[0]?.type : item?.value?.type;

      if (
        itemType === ModuleType.HR ||
        itemType === ModuleType.Contractor ||
        itemType === ModuleType.Incident
      ) {
        itemValue = item?.value[0];
      }

      return {
        value: itemValue,
        _id: item._id,
        type: ModuleType.SysField,
      };
    })
    ?.filter(({ value }) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });

  // formats all values, then takes only the cusvals
  const formattedCusvals = formatCusvalValueToPayload(values, cusvalConfig);
  const cusvals = (formattedCusvals || []).map((item: any) => ({
    _id: item?._id,
    type: item?.type,
    title: item?.title,
    value: item?.value,
  }));

  return {
    sysFields,
    cusvals,
  };
};

export const formatSysFieldToForm = (sysFields: OhsSysFieldValue[]) =>
  (sysFields || []).map((field) => {
    // link hr contractor field only accepts array but submit sysfield only accept object
    return field.fieldType === OhsCusvalType.LinkedValue && field.value?._id
      ? { ...field, value: [field.value] }
      : field;
  });
